import { useList, useDelete, useUpdate } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  StatusBadge,
  getExpiryStatus,
} from '@/components/shared';
import {
  DataTable,
  TableToolbar,
  type Column,
  type SortState,
  type BulkAction,
} from '@/components/tables';
import { FormHeader } from '@/components/forms';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  ExternalLink,
  MousePointerClick,
  TrendingUp,
  ArrowUpDown,
  ImageIcon,
  Users,
} from 'lucide-react';
import { ReorderModal } from '@/components/modals';
import {
  type MiniBanner,
  calculateMiniBannerCTR,
  formatMiniBannerTargetLabel,
  getMiniBannerThumbnailUrl,
  getMiniBannerTitle,
} from './types';
import { MiniBannerEventsDialog, type EventView } from './MiniBannerEventsDialog';
import { notify } from '@/lib/notify';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';

function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
}

/**
 * Mini-banners list — структура 1:1 как у обычных баннеров:
 *   bulk-actions с чекбоксами, фильтры, сортировка, просмотр событий (views/clicks),
 *   reorder-модалка, статусный badge.
 */
export function MiniBannersList() {
  const navigate = useNavigate();
  useScrollRestoration();
  const [page, setPage] = usePersistedState('mini-banners:page', 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>('mini-banners:sort', { field: 'order', order: 'asc' });
  const [search, setSearch] = usePersistedState('mini-banners:search', '');
  const [activeFilter, setActiveFilter] = usePersistedState<string>('mini-banners:activeFilter', 'all');
  const [deleteItems, setDeleteItems] = useState<MiniBanner[]>([]);
  const [reorderOpen, setReorderOpen] = useState(false);
  const [eventsDialog, setEventsDialog] = useState<{ banner: MiniBanner; type: EventView } | null>(null);
  const pageSize = 20;

  const { result, query } = useList<MiniBanner>({
    resource: 'mini-banners',
    pagination: { currentPage: page, pageSize, mode: 'server' },
    filters: [
      ...(activeFilter !== 'all'
        ? [
            {
              field: 'isActive',
              operator: 'eq' as const,
              value: activeFilter === 'true',
            },
          ]
        : []),
      ...(search
        ? [{ field: 'search', operator: 'contains' as const, value: search }]
        : []),
    ],
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  const { mutate: deleteBanner } = useDelete();
  const { mutate: updateBanner } = useUpdate();

  const banners = result?.data ?? [];
  const total = result?.total ?? 0;
  const isLoading = query.isLoading;

  const handleToggleActive = (items: MiniBanner[]) => {
    if (items.length === 0) { return; }
    const targetActive = !items[0].isActive;
    items.forEach((banner) => {
      updateBanner({
        resource: 'mini-banners',
        id: banner.id,
        values: { isActive: !banner.isActive },
        successNotification: false,
      });
    });
    notify.success(
      targetActive
        ? `Активировано: ${items.length}`
        : `Деактивировано: ${items.length}`,
    );
  };

  const handleDelete = () => {
    if (deleteItems.length === 0) { return; }

    const count = deleteItems.length;
    for (const item of deleteItems) {
      deleteBanner({ resource: 'mini-banners', id: item.id, successNotification: false });
    }
    notify.success(`Удалено: ${count}`);
    setDeleteItems([]);
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) { return '—'; }
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const columns: Column<MiniBanner>[] = [
    {
      key: 'thumbnail',
      header: 'Баннер',
      render: (_, banner) => {
        const bgUrl = getMiniBannerThumbnailUrl(banner);
        const title = getMiniBannerTitle(banner);
        return (
          <div className="flex items-center gap-3">
            {bgUrl ? (
              <img
                src={bgUrl}
                alt={`Мини-баннер #${banner.id}`}
                className="h-10 w-24 object-cover rounded"
              />
            ) : (
              <div className="h-10 w-24 bg-muted rounded flex items-center justify-center">
                <ImageIcon className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
            <div className="min-w-0">
              <p className="font-medium truncate max-w-[220px]">
                {title ?? `Мини-баннер #${banner.id}`}
              </p>
              <p className="text-sm text-muted-foreground">
                {!banner.startDate && !banner.endDate
                  ? 'Бессрочно'
                  : `${banner.startDate ? formatDate(banner.startDate) : '...'} — ${banner.endDate ? formatDate(banner.endDate) : 'Бессрочно'}`}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      key: 'targets',
      header: 'Где показывается',
      hiddenOnMobile: true,
      render: (_, banner) => {
        if (banner.targets.length === 0) {
          return <span className="text-muted-foreground">—</span>;
        }
        const first = banner.targets[0];
        if (!first) { return '—'; }
        const firstLabel = formatMiniBannerTargetLabel(first);
        const rest = banner.targets.length - 1;
        return (
          <div className="text-sm min-w-0 max-w-[320px]">
            <p className="truncate">{firstLabel}</p>
            {rest > 0 && (
              <p className="text-xs text-muted-foreground">+ ещё {rest}</p>
            )}
          </div>
        );
      },
    },
    {
      key: 'stats',
      header: 'Статистика',
      width: '300px',
      hiddenOnMobile: true,
      render: (_, banner) => (
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <button
            type="button"
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            title="Просмотры (всего)"
            onClick={(e) => {
              e.stopPropagation();
              setEventsDialog({ banner, type: 'views' });
            }}
          >
            <Eye className="h-3.5 w-3.5" />
            {formatNumber(banner.viewsCount || 0)}
          </button>
          <button
            type="button"
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            title="Уникальные просмотры"
            onClick={(e) => {
              e.stopPropagation();
              setEventsDialog({ banner, type: 'unique-views' });
            }}
          >
            <Users className="h-3.5 w-3.5" />
            {formatNumber(banner.uniqueViewsCount || 0)}
          </button>
          <button
            type="button"
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            title="Клики (всего)"
            onClick={(e) => {
              e.stopPropagation();
              setEventsDialog({ banner, type: 'clicks' });
            }}
          >
            <MousePointerClick className="h-3.5 w-3.5" />
            {formatNumber(banner.clicksCount || 0)}
          </button>
          <button
            type="button"
            className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors"
            title="Уникальные клики"
            onClick={(e) => {
              e.stopPropagation();
              setEventsDialog({ banner, type: 'unique-clicks' });
            }}
          >
            <MousePointerClick className="h-3.5 w-3.5" />
            <Users className="h-3 w-3 -ml-0.5" />
            {formatNumber(banner.uniqueClicksCount || 0)}
          </button>
          <span className="flex items-center gap-1" title="CTR">
            <TrendingUp className="h-3.5 w-3.5" />
            {calculateMiniBannerCTR(banner).toFixed(1)}%
          </span>
        </div>
      ),
    },
    {
      key: 'linkUrl',
      header: 'Ссылка',
      width: '80px',
      hiddenOnMobile: true,
      render: (_, banner) =>
        banner.linkUrl ? (
          <a
            href={banner.linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-foreground"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'status',
      header: 'Статус',
      width: '140px',
      render: (_, banner) => {
        if (!banner.isActive) {
          return <StatusBadge status="disabled" customLabel="Выключен" size="sm" />;
        }
        const expiryStatus = getExpiryStatus(
          banner.startDate ? new Date(banner.startDate) : null,
          banner.endDate ? new Date(banner.endDate) : null,
        );
        if (expiryStatus === 'expired') {
          return <StatusBadge status="expired" size="sm" />;
        }
        if (expiryStatus === 'scheduled') {
          return <StatusBadge status="scheduled" size="sm" />;
        }
        return <StatusBadge status="active" size="sm" />;
      },
    },
  ];

  const bulkActions: BulkAction<MiniBanner>[] = [
    {
      label: 'Редактировать',
      icon: <Pencil className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          navigate(`/mini-banners/edit/${items[0].id}`);
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Активировать',
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => handleToggleActive(items.filter((i) => !i.isActive)),
      showOnlyWhenSelected: true,
    },
    {
      label: 'Деактивировать',
      icon: <EyeOff className="h-4 w-4" />,
      onClick: (items) => handleToggleActive(items.filter((i) => i.isActive)),
      variant: 'outline',
      showOnlyWhenSelected: true,
    },
    {
      label: 'Удалить',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: setDeleteItems,
      variant: 'destructive',
      showOnlyWhenSelected: true,
    },
  ];

  const filtersCount = activeFilter !== 'all' ? 1 : 0;

  return (
    <div className="space-y-6">
      <FormHeader
        title="Мини-баннеры"
        description="Карточки, показываемые внутри урока между контентом и кнопкой «Следующий урок»"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setReorderOpen(true)}>
              <ArrowUpDown className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Порядок</span>
            </Button>
            <Button size="sm" onClick={() => navigate('/mini-banners/create')}>
              <Plus className="h-4 w-4 md:mr-2" />
              <span className="hidden md:inline">Добавить</span>
            </Button>
          </div>
        }
        sticky
      />

      <TableToolbar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Поиск по ID..."
        filters={
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="mini-banner-active">Статус активности</Label>
              <Select value={activeFilter} onValueChange={setActiveFilter}>
                <SelectTrigger id="mini-banner-active">
                  <SelectValue placeholder="Статус" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все</SelectItem>
                  <SelectItem value="true">Активные</SelectItem>
                  <SelectItem value="false">Неактивные</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        }
        filtersCount={filtersCount}
        onResetFilters={() => {
          setSearch('');
          setActiveFilter('all');
        }}
      />

      <DataTable
        columns={columns}
        data={banners}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(banner) => navigate(`/mini-banners/edit/${banner.id}`)}
        emptyMessage="Мини-баннеры не найдены"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      <AlertDialog open={deleteItems.length > 0} onOpenChange={() => setDeleteItems([])}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteItems.length === 1
                ? 'Удалить мини-баннер?'
                : `Удалить ${deleteItems.length} мини-баннеров?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteItems.length === 1 ? (
                <>
                  Вы уверены, что хотите удалить мини-баннер #{deleteItems[0].id}?
                  Это действие нельзя отменить.
                </>
              ) : (
                <>
                  Вы уверены, что хотите удалить {deleteItems.length} мини-баннеров?
                  Это действие нельзя отменить.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <MiniBannerEventsDialog
        banner={eventsDialog?.banner ?? null}
        type={eventsDialog?.type ?? null}
        open={eventsDialog !== null}
        onOpenChange={(open) => {
          if (!open) { setEventsDialog(null); }
        }}
      />

      <ReorderModal
        open={reorderOpen}
        onOpenChange={setReorderOpen}
        resource="mini-banners"
        items={banners}
        title="Порядок мини-баннеров"
        renderItem={(banner) => {
          const bgUrl = getMiniBannerThumbnailUrl(banner);
          const title = getMiniBannerTitle(banner);
          return (
            <div className="flex items-center gap-3">
              {bgUrl ? (
                <img
                  src={bgUrl}
                  alt=""
                  className="h-10 w-24 object-cover rounded"
                />
              ) : (
                <div className="h-10 w-24 bg-muted rounded flex items-center justify-center">
                  <ImageIcon className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
              <div>
                <p className="font-medium text-sm">{title ?? `Мини-баннер #${banner.id}`}</p>
                <p className="text-xs text-muted-foreground">
                  {banner.targets.length > 0
                    ? formatMiniBannerTargetLabel(banner.targets[0])
                    : 'нет целей'}
                </p>
              </div>
            </div>
          );
        }}
        onSuccess={() => query.refetch()}
      />
    </div>
  );
}
